import { GeoJSONGeometry } from "ol/format/GeoJSON";
import * as farmsApi from "@/api/farms/index";
import * as carApi from '@/api/car/index';
import store from "@/store";
import * as mt from "@/store/farms/mutation-types";
import { Owner } from "@/api/owner/types";
import { Farm, FarmDTO, FarmListFilter } from "@/api/farms/types";
import * as notificationService from "../notification/index";
import i18n from "@/i18n";
import EventBus from "@/event-bus";
import { Page } from "@/api/types";

export async function clearFarms() {
  await store.dispatch(`farms/${mt.CLEAR_FARMS}`);
}

export async function getFarmsByOwner(owner: Owner) {
  await store.dispatch(`farms/${mt.FARMS_REQUEST}`);

  try {
    const farms = (await farmsApi.getFarmsByOwner(owner.uuid!)).data;
    const sortedFarms = farms.sort((a: Farm, b: Farm) => a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1);
    await store.dispatch(`farms/${mt.FARMS_REQUEST_SUCCESS}`, farms);
  } catch (err) {
    await store.dispatch(`farms/${mt.FARMS_REQUEST_ERROR}`);
  }
}

export async function createFarm(farm: FarmDTO) {
  store.dispatch(`farms/${mt.CREATE_FARMS_REQUEST}`);
  try {
    const createdFarm = (await farmsApi.createFarm(farm)).data;
    store.dispatch(`farms/${mt.CREATE_FARMS_SUCCESS}`);
    notificationService.addSuccessNotification(
      i18n.t("notifications.success.farmMessage", {
        farmName: farm.name
      }) as string
    );
    await getFarmsByOwner(createdFarm.owner);
  } catch (err) {
    store.dispatch(`farms/${mt.CREATE_FARMS_FAILURE}`);
    if (err.response && err.response.status === 429) {
      notificationService.addErrorNotification(
        i18n.t("notifications.error.trial-farm-limit-exceeded",{
          farmsCount: store.getters['user/farmsCount']
        }) as string
      );
      return;
    }

    notificationService.addErrorNotification(
      i18n.t("notifications.error.message") as string
    );
  } finally {
    EventBus.$emit("update:farms");
  }
}

export async function createFarmWithoutStore(farm: FarmDTO) {
  try {
    const createdFarm = (await farmsApi.createFarm(farm)).data;
    notificationService.addSuccessNotification(
      i18n.t("notifications.success.farmMessage", {
        farmName: farm.name
      }) as string
    );
    return createdFarm;
  } catch (err) {
    if (err.response && err.response.status === 429) {
      notificationService.addErrorNotification(
        i18n.t("notifications.error.trial-farm-limit-exceeded",{
          farmsCount: store.getters['user/farmsCount']
        }) as string
      );
    } else {
      notificationService.addErrorNotification(
        i18n.t("notifications.error.message") as string
      );
    }

    throw err;
  } finally {
    EventBus.$emit("update:farms");
  }
}


export async function deleteFarm(uuid: string) {
  try {
    await farmsApi.deleteFarm(uuid!);
    store.dispatch(`farms/${mt.FARM_DELETE}`);
    notificationService.addSuccessNotification(
      i18n.t("notifications.success.delete-farm") as string
    );
  } catch (err) {
    store.dispatch(`farms/${mt.FARM_DELETE_ERROR}`);
    notificationService.addErrorNotification(
      i18n.t("notifications.error.delete-farm") as string
    );
  } finally {
    EventBus.$emit("update:farms");
  }
}


export async function clearFarmList() {
  await store.dispatch(`farms/${mt.FARM_LIST_CLEAR}`);
}

function setLngLatFilter(f: FarmListFilter) {
  const filter = { ...f }
  if (filter.lngLat) {
    const valid = /^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/.test(filter.lngLat.trim());
    if (valid) {
      filter.lon = filter.lngLat.split(',')[0].trim();
      filter.lat = filter.lngLat.split(',')[1].trim();
    } else {
      filter.lon = '999';
      filter.lat = '999';
    }
  }
  delete filter.lngLat;
  return filter;
}

export async function getFarmsList(page: Page, filters: FarmListFilter) {
  await store.dispatch(`farms/${mt.FARM_LIST_REQUEST}`);
  try {
    const response = (await farmsApi.getFarmList(page, setLngLatFilter(filters)));
    await store.dispatch(`farms/${mt.FARM_LIST_SUCCESS}`, { farmList: response.data });
    return response;
  } catch (err) {
    await store.dispatch(`farms/${mt.FARM_LIST_ERROR}`);
    throw err;
  }
}

export async function getFarmsCentroids(filter: FarmListFilter) {
  return (await farmsApi.getFarmsCentroids(setLngLatFilter(filter))).data;
}

export async function clearFilter() {
  await store.dispatch(`farms/${mt.FARM_LIST_FILTER_CLEAR}`);
}

export async function setFilter(filter: FarmListFilter) {
  await store.dispatch(`farms/${mt.FARM_LIST_FILTER_PERSIST}`, {filter: filter});
}

export async function getCarsNearByGeom(long: number, lat: number) {
  return (await carApi.findCarsNearByGeom(long, lat))?.data;
}
